import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://xperiencetattoo.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/main-vlc-image.webp`;

const LOCALE_MAP: Record<string, string> = {
  en: 'en_US',
  es: 'es_ES',
  de: 'de_DE',
};

const LANGUAGES = ['en', 'es', 'de'];

interface SEOProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  jsonLd?: Record<string, any>[];
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalPath,
  ogImage,
  jsonLd,
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const currentLang = i18n.language?.split('-')[0] || 'en';
  const currentLocale = LOCALE_MAP[currentLang] || 'en_US';

  const path = canonicalPath || location.pathname.replace(/^\/(en|es|de)/, '') || '/';
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  const langPrefixMatch = location.pathname.match(/^\/(en|es|de)/);
  const canonicalUrl = langPrefixMatch
    ? `${SITE_URL}/${langPrefixMatch[1]}${cleanPath}`
    : `${SITE_URL}${cleanPath}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />

      <link rel="canonical" href={canonicalUrl} />

      {LANGUAGES.map(lang => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={`${SITE_URL}/${lang}${cleanPath}`}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${SITE_URL}${cleanPath}`}
      />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage || DEFAULT_OG_IMAGE} />
      <meta property="og:locale" content={currentLocale} />
      <meta property="og:site_name" content="XperienceTattoo" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage || DEFAULT_OG_IMAGE} />

      {jsonLd && jsonLd.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
